import { createActorContext, useSelector } from '@xstate/react';
import { Context, createContext, useCallback, useContext, useMemo } from 'react';
import { ActorRefFrom, AnyStateMachine } from 'xstate';

export type ActorContextFrom<T extends AnyStateMachine> = Omit<ReturnType<typeof createActorContext<T>>, 'Provider'>;

/** Given an actorRef, returns useSelector and useActorRef hooks as you would get them from createActorContext */
export const useActorUtilities = <T extends AnyStateMachine>(actorRef: ActorRefFrom<T>): ActorContextFrom<T> => {
  const useActorInternal: ActorContextFrom<T>['useActorRef'] = useCallback(() => {
    return actorRef;
  }, [actorRef]);

  const useSelectorInternal: ActorContextFrom<T>['useSelector'] = useCallback(
    (selector, compare) => {
      // @ts-expect-error  @note(juan): this is probably an issue in the library itself
      return useSelector(actorRef, selector, compare);
    },
    [actorRef],
  );

  const useActorRef: ActorContextFrom<T>['useActorRef'] = useCallback(() => actorRef, [actorRef]);

  const data = useMemo(
    () => ({ useActor: useActorInternal, useSelector: useSelectorInternal, useActorRef }),
    [actorRef],
  );

  return data;
};

/**
 * Should function identically to createActorContext with the only difference that this accepts an actor (machine) that is
 * already created and is already running (usually as a child invoked machine)
 */
export const createActorContextFromActor = <T extends AnyStateMachine>(): {
  useActorRef: ReturnType<typeof createActorContext<T>>['useActorRef'];
  useSelector: ReturnType<typeof createActorContext<T>>['useSelector'];
  Provider: Context<ActorContextFrom<T> | null>['Provider'];
} => {
  const ActorContext = createContext<ActorContextFrom<T> | null>(null);

  const useContextInternal = () => {
    const actor = useContext(ActorContext);

    if (!actor) {
      throw new Error(`You tried to access an XState context outside its Provider`);
    }

    return actor;
  };

  return {
    useActorRef: () => useContextInternal().useActorRef(),
    useSelector: (...params) => useContextInternal().useSelector(...params),
    Provider: ActorContext.Provider,
  };
};
